import { Injectable } from "@angular/core";
import { ItemWithRelations, MarkingPlace, SignaturePlace } from "src/app/api/models";
import { ItemService } from "./item.service";
import { AuthorService } from "../shared/services/author.service";
import { PictureService } from "./picture.service";
import { SignatureService } from "./signature.service";
import { MarkingService } from "./marking.service";
import {
  EditorContactsControllerService,
  MarkingPlaceControllerService,
  MarkingTypeControllerService,
  SignaturePlaceControllerService,
} from "src/app/api/services";
import { lastValueFrom } from "rxjs";
import ExcelJS from "exceljs";

const PHOTO_COL = 2; // 1-based column index of the photo column
const ROW_HEIGHT = 80; // points — height for rows that have an embedded image

@Injectable({
  providedIn: "root",
})
export class ItemsXlsExportService {
  constructor(
    private itemService: ItemService,
    private authorService: AuthorService,
    private pictureService: PictureService,
    private signatureService: SignatureService,
    private signaturePlaceService: SignaturePlaceControllerService,
    private markingService: MarkingService,
    private markingPlaceService: MarkingPlaceControllerService,
    private markingTypeService: MarkingTypeControllerService,
    private contactsService: EditorContactsControllerService,
  ) {}

  async exportItems(
    items: ItemWithRelations[],
    filename: string = "items",
  ): Promise<void> {
    const auditContactIds = [
      ...new Set(items.flatMap((i) => i.audits?.map((a) => a.contactId) ?? [])),
    ];
    const [workbook, signaturePlaces, markingPlaces, markingTypes, fetchedAuditContacts] =
      await Promise.all([
        Promise.resolve(new ExcelJS.Workbook()),
        lastValueFrom(this.signaturePlaceService.getSignaturePlacesList()),
        lastValueFrom(this.markingPlaceService.getMarkingPlacesList()),
        lastValueFrom(this.markingTypeService.getMarkingTypesList()),
        Promise.all(
          auditContactIds.map((id) =>
            lastValueFrom(this.contactsService.editorGetContact({ id })).catch(() => null),
          ),
        ),
      ]);
    const auditContactMap = new Map(
      fetchedAuditContacts.filter(Boolean).map((c) => [c!.id, c!.title]),
    );
    const sheet = workbook.addWorksheet("Díla");

    sheet.columns = [
      { header: "Inventární číslo", key: "invCode", width: 22 },
      { header: "Fotografie", key: "photo", width: 18 },
      { header: "Název", key: "title", width: 40 },
      { header: "Autoři", key: "authors", width: 30 },
      { header: "Autor - jméno", key: "authorFirstname", width: 20 },
      { header: "Autor - příjmení", key: "authorLastname", width: 20 },
      { header: "Datace", key: "dating", width: 15 },
      { header: "Datace k řazení", key: "datingOrder", width: 15 },
      { header: "Typ díla", key: "type", width: 20 },
      { header: "Typ spornosti", key: "disputationType", width: 20 },
      { header: "Specifika", key: "extras", width: 25 },
      { header: "Technika", key: "technique", width: 25 },
      { header: "Podložka", key: "substrate", width: 25 },
      { header: "Šířka (cm)", key: "width", width: 12 },
      { header: "Výška (cm)", key: "height", width: 12 },
      { header: "Hloubka (cm)", key: "depth", width: 12 },
      { header: "Šířka alt. (cm)", key: "altWidth", width: 14 },
      { header: "Výška alt. (cm)", key: "altHeight", width: 14 },
      { header: "Hloubka alt. (cm)", key: "altDepth", width: 14 },
      { header: "Lokace", key: "location", width: 20 },
      { header: "Signatura", key: "signature", width: 30 },
      { header: "Značení", key: "markings", width: 30 },
      { header: "Série", key: "series", width: 25 },
      { header: "Klíčová slova", key: "tags", width: 30 },
      { header: "Současný majitel", key: "currentOwner", width: 35 },
      { header: "Pozůstalost", key: "estate", width: 14 },
      { header: "Rub díla", key: "hasOtherSide", width: 12 },
      { header: "Tisková kvalita", key: "printQuality", width: 14 },
      {
        header: "Povolení k reprodukci",
        key: "permissionReproduce",
        width: 20,
      },
      { header: "Veřejné", key: "public", width: 10 },
      { header: "Publikováno", key: "published", width: 12 },
      { header: "Přeloženo", key: "translated", width: 12 },
      { header: "Kompletováno", key: "completed", width: 14 },
      { header: "Viděno", key: "seen", width: 10 },
      { header: "Viděno kým", key: "seenByContacts", width: 35 },
      { header: "Audity", key: "audits", width: 30 },
      { header: "Počet fotografií", key: "photoCount", width: 16 },
      { header: "Počet výstav", key: "exhibitionCount", width: 14 },
      { header: "Počet publikací", key: "publicationCount", width: 16 },
      { header: "Počet aukcí", key: "auctionCount", width: 12 },
    ];

    sheet.getRow(1).font = { bold: true };

    // Phase 1: add all data rows so ExcelJS has them materialized before images are placed
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const csTranslation = item.translations?.find((t) => t.localeId === 1);
      const row = sheet.addRow({
        invCode: this.itemService.getCompleteInventoryCode(item),
        photo: "",
        title: item.originalTitle ?? "",
        authors: this.buildAuthorsString(item),
        authorFirstname: item.authors?.[0]?.firstname ?? "",
        authorLastname: item.authors?.[0]?.lastname ?? "",
        dating: this.itemService.parseItemDating(item),
        datingOrder: item.datingOrder ?? "",
        type:
          item.type?.langs?.[0]?.name ??
          item.type?.lang?.name ??
          item.type?.code ??
          "",
        disputationType:
          item.itemDisputationType?.langs?.[0]?.name ??
          item.itemDisputationType?.lang?.name ??
          item.itemDisputationType?.code ??
          "",
        extras: item.extras?.map((e) => e.title).join(", ") ?? "",
        technique: csTranslation?.technique ?? "",
        substrate: csTranslation?.substrate ?? "",
        width: item.dimensionWidth ?? "",
        height: item.dimensionHeight ?? "",
        depth: item.dimensionDepth ?? "",
        altWidth: item.dimensionAltWidth ?? "",
        altHeight: item.dimensionAltHeight ?? "",
        altDepth: item.dimensionAltDepth ?? "",
        location: item.location ? this.buildLocationPath(item.location) : "",
        signature: this.buildSignatureString(item, signaturePlaces),
        markings: this.buildMarkingString(item, markingPlaces, markingTypes),
        series:
          item.series
            ?.map((s) => s.lang?.title)
            .filter(Boolean)
            .join(", ") ?? "",
        tags: item.tags?.map((t) => t.titleCS).join(", ") ?? "",
        currentOwner: (() => {
          const name = item.ownerCurrent ?? "";
          const owner = item.owners?.find((o) => o.ownerTypeId === 1);
          return owner?.ownerItemId ? `${name} (${owner.ownerItemId})` : name;
        })(),
        estate: item.estate ? "Ano" : "Ne",
        hasOtherSide: item.hasOtherSide ? "Ano" : "Ne",
        printQuality: item.printQuality ? "Ano" : "Ne",
        permissionReproduce: item.permissionReproduce ? "Ano" : "Ne",
        public: item.public ? "Ano" : "Ne",
        published: item.published ? "Ano" : "Ne",
        translated: item.translated ? "Ano" : "Ne",
        completed: item.completed ? "Ano" : "Ne",
        seen: item.seen ? "Ano" : "Ne",
        seenByContacts: item.seenByContacts?.map((c) => c.title).filter(Boolean).join(", ") ?? "",
        audits: item.audits
          ?.map((a) => {
            const date = new Date(a.date).toLocaleDateString("cs-CZ");
            const contact = auditContactMap.get(a.contactId) ?? "";
            const header = [date, contact].filter(Boolean).join(" – ");
            return a.comment ? `- ${header}: ${this.stripHtml(a.comment)}` : `- ${header}`;
          })
          .join("\n") ?? "",
        photoCount: item.files?.filter((f) => f.type === 1).length ?? 0,
        exhibitionCount: item.itemExhibitions?.length ?? 0,
        publicationCount: item.itemPublications?.length ?? 0,
        auctionCount: item.itemAuctions?.length ?? 0,
      });
      row.height = ROW_HEIGHT;
    }

    // Phase 2: fetch images in parallel and embed — all rows already exist so br won't create phantom rows
    await Promise.all(
      items.map(async (item, i) => {
        const excelRowIndex = i + 2;
        const mainPicture = this.pictureService.getAllPictures(item, false)[0];
        if (!mainPicture) return;
        try {
          const url = this.buildPhotoUrl(mainPicture.filename, mainPicture.ext);
          const base64 = await this.fetchImageAsBase64(url);
          const imageId = workbook.addImage({ base64, extension: "jpeg" });
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (sheet as any).addImage(imageId, {
            tl: { col: PHOTO_COL - 1, row: excelRowIndex - 1 },
            br: { col: PHOTO_COL, row: excelRowIndex },
            editAs: "twoCell",
          });
        } catch {
          // skip image on fetch failure
        }
      }),
    );

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer as ArrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ---------------------------------------------------------------------------
  // Image helpers
  // ---------------------------------------------------------------------------

  private buildPhotoUrl(filename: string, ext: string): string {
    // Request JPEG — exceljs doesn't support webp
    return this.pictureService.getPath(filename + ext).replace(/\.webp$/, ".jpeg");
  }

  private async fetchImageAsBase64(url: string): Promise<string> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = "";
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary);
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private buildAuthorsString(item: ItemWithRelations): string {
    if (!item.authors || item.authors.length === 0) return "Bez autora";
    return item.authors
      .map((a) => this.authorService.parseAuthorName(a))
      .join(", ");
  }

  private buildSignatureString(item: ItemWithRelations, signaturePlaces: SignaturePlace[]): string {
    if (!item.signatures || item.signatures.length === 0) return "";
    return item.signatures
      .map((s) => {
        const setup = this.signatureService.getSignatureSetup(s, signaturePlaces);
        const label = this.stripHtml(setup.label);
        const shortcut = setup.shortcut ? ` (${setup.shortcut})` : "";
        const comment = s.commentCS ? `\n  ${this.stripHtml(s.commentCS)}` : "";
        return `- ${label}${shortcut}${comment}`;
      })
      .join("\n");
  }

  private buildMarkingString(item: ItemWithRelations, markingPlaces: MarkingPlace[], markingTypes: any[]): string {
    if (!item.markings || item.markings.length === 0) return "";
    return item.markings
      .map((m) => {
        const setup = this.markingService.getMarkingSetup(m, markingPlaces, markingTypes);
        const label = this.stripHtml(setup.label);
        const shortcut = setup.shortcut ? ` (${setup.shortcut})` : "";
        const comment = m.commentCS ? `\n  ${this.stripHtml(m.commentCS)}` : "";
        return `- ${label}${shortcut}${comment}`;
      })
      .join("\n");
  }

  private buildLocationPath(location: { title: string; parent?: { title: string; parent?: any } }): string {
    const parts: string[] = [];
    let current: { title: string; parent?: any } | undefined = location;
    while (current) {
      parts.unshift(current.title);
      current = current.parent;
    }
    return parts.join(" > ");
  }

  private stripHtml(html: string): string {
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent ?? div.innerText ?? "").replace(/\s+/g, " ").trim();
  }
}
